import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, Form, Input } from 'antd';
import { Rule } from 'antd/es/form';
import { CardMainLayout, Catcher, FormSelect, InputDebounce, cardLayoutOptions } from '../../';
import dayjs from 'dayjs';
import { FieldData } from 'rc-field-form/es/interface';
import { IFormFields, NotificationType } from '../../../interfaces';
import { useBillingCompaniesSelect, useCompanyInfo } from '../../../hooks';
import {
    addNotification,
    apiMessagesGetFormRules,
    deleteBillingCompaniesInvoiceNumber,
    getBillingCompaniesSelect,
    getCompanyById,
    postBillingCompaniesInvoiceNumber,
} from '../../../middleware';
import Styles from '../../styles/m_itemCardStyles.less';

const { TextArea } = Input;

interface IEditCompanyInvoiceFormProps {
    companyId: string;
    onFieldsChange: (changedFields: FieldData[]) => void;
}

export const EditCompanyInvoiceForm: React.FC<IEditCompanyInvoiceFormProps> = observer(
    ({ companyId, onFieldsChange }: IEditCompanyInvoiceFormProps) => {
        const companyInfo = useCompanyInfo();
        const [form] = Form.useForm();
        const initCompanyCode = companyInfo.companyCode;
        const invoicedByCompanyId = companyInfo.invoicedByCompanyId;
        const [isSending, setIsSending] = React.useState(false);
        const [isInitCompanyId, setIsInitCompanyId] = React.useState(false);
        const [invoice, setInvoice] = React.useState<boolean>(false);
        const initialValues = {
            invoicingCompany: !!initCompanyCode,
            invoiceCompanyCode: initCompanyCode,
            accountNumber: companyInfo.accountNumber,
            swift: companyInfo.swift,
            iban: companyInfo.iban,
            invoiceFooter: companyInfo.invoiceFooter,
        };

        const validateCompanyCode: Array<Rule> = [
            { required: true, message: 'Company code required' },
            ...apiMessagesGetFormRules,
        ];

        const fetchBillingCompaniesSelect = async () => {
            await getBillingCompaniesSelect({ isInvoicing: true });
        };

        useEffect(() => {
            fetchBillingCompaniesSelect();
        }, [invoice]);

        useEffect(() => {
            if (invoicedByCompanyId && !isInitCompanyId) {
                form.setFieldValue('invoicedByCompanyId', invoicedByCompanyId);
                setIsInitCompanyId(true);
            }
        }, [invoicedByCompanyId]);

        useEffect(() => {
            if (initialValues.invoiceCompanyCode) {
                setInvoice(true);
            }

            if (invoicedByCompanyId) {
                form.setFieldValue('invoicedByCompanyId', invoicedByCompanyId);
            }

            form.setFieldsValue(initialValues);
        }, [JSON.stringify(initialValues)]);

        const onSave = async (): Promise<void> => {
            setIsSending(true);
            form.validateFields().then();

            const formValues = form.getFieldsValue() as IFormFields;

            const body = {
                invoice_configuration: {
                    company_code: formValues.invoiceCompanyCode,
                    account_number: formValues.accountNumber,
                    invoice_footer: formValues.invoiceFooter,
                    iban: formValues.iban,
                    swift: formValues.swift,
                },
            };

            const { isError, resp } = await postBillingCompaniesInvoiceNumber(body, companyId, !!initCompanyCode);

            if (isError) {
                const { company_code } = resp as { company_code: string[] };

                addNotification({
                    type: NotificationType.ERROR,
                    message: `Company Code - ${company_code?.[0]}`,
                });

                setIsSending(false);
                form.validateFields().then();
                return;
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: !initCompanyCode ? 'Added Company Invoicing' : 'Updated Company Invoicing',
            });

            await getCompanyById(companyId);

            setIsSending(false);
        };

        const onInvoiceCheck = () => {
            setInvoice(!invoice);
        };

        const onDelete = async () => {
            setIsSending(true);

            const { isError } = await deleteBillingCompaniesInvoiceNumber(companyId);

            if (isError) {
                addNotification({
                    type: NotificationType.ERROR,
                    message: `Company Code deletion error}`,
                });

                setIsSending(false);
                form.validateFields().then();
                form.setFieldValue('invoiceCompanyCode', null);
                return;
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Deleted successfully!',
            });

            await getCompanyById(companyId);

            setIsSending(false);
        };

        const onFormFieldChange = (changedFields: FieldData[]) => {
            onFieldsChange(changedFields);
        };

        return (
            <Catcher>
                <CardMainLayout
                    columnOption={cardLayoutOptions.cardEditCompanyCustomize}
                    idSpinners={[
                        'POST_BILLING_SYSTEM_INVOICE_NUMBER',
                        'PUT_BILLING_SYSTEM_INVOICE_NUMBER',
                        'DELETE_BILLING_SYSTEM_INVOICE_NUMBER',
                        'GET_BILLING_SYSTEM_COMPANIES_SELECT',
                    ]}
                    name={'Company Invoicing'}
                    dataTest={'EditCompanyInvoicingInfo'}
                >
                    <div className={Styles.companyInvoiceForm}>
                        <Form.Item name="invoicingCompany" rules={apiMessagesGetFormRules}>
                            <Checkbox onChange={onInvoiceCheck} checked={invoice}>
                                Invoicing Company
                            </Checkbox>
                        </Form.Item>
                        <Form
                            form={form}
                            name="companyInvoicing"
                            scrollToFirstError={true}
                            layout={'vertical'}
                            className={'formStyle'}
                            validateTrigger={'onChange'}
                            onFieldsChange={!invoice ? onFormFieldChange : undefined}
                        >
                            {invoice ? (
                                <>
                                    <Form.Item
                                        name="invoiceCompanyCode"
                                        label={'Company Code'}
                                        rules={validateCompanyCode}
                                    >
                                        <InputDebounce placeholder={'Company Code'} />
                                    </Form.Item>
                                    <p className={Styles.companyInvoiceFormatExample}>
                                        Example: {initCompanyCode}
                                        {dayjs().format('YYYYMMDD')}-001
                                    </p>

                                    <Form.Item name="iban" label={'IBAN'} rules={apiMessagesGetFormRules}>
                                        <InputDebounce placeholder={'IBAN'} />
                                    </Form.Item>

                                    <Form.Item name="swift" label={'SWIFT'} rules={apiMessagesGetFormRules}>
                                        <InputDebounce placeholder={'SWIFT'} />
                                    </Form.Item>

                                    <Form.Item
                                        name="accountNumber"
                                        label={'Account Number'}
                                        rules={apiMessagesGetFormRules}
                                    >
                                        <InputDebounce placeholder={'Account Number'} />
                                    </Form.Item>

                                    <Form.Item
                                        name="invoiceFooter"
                                        label={'Invoice Footer'}
                                        rules={apiMessagesGetFormRules}
                                    >
                                        <TextArea placeholder={'Invoice Footer Text'} rows={4} maxLength={500} />
                                    </Form.Item>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Button type={'primary'} onClick={onSave} loading={isSending}>
                                            Save
                                        </Button>
                                        {initCompanyCode ? (
                                            <Button danger type={'primary'} loading={isSending} onClick={onDelete}>
                                                Delete
                                            </Button>
                                        ) : null}
                                    </div>
                                </>
                            ) : null}
                            {!invoice ? (
                                <FormSelect
                                    fieldName={'invoicedByCompanyId'}
                                    label={'Customer of:'}
                                    rules={apiMessagesGetFormRules}
                                    useSelectHook={() => useBillingCompaniesSelect(companyId)}
                                />
                            ) : null}
                        </Form>
                    </div>
                </CardMainLayout>
            </Catcher>
        );
    },
);
